import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { ApiResponse } from '../../menu/types';
import { invalidateGridDataCache } from './invalidateGridDataCache';

export const FINANCE_EMAIL_HISTORY_ACTION_API = '/umbraco/api/FinanceEmailHistoryAction';

type ActionResponse = {
  message?: string;
};

const parseActionResponse = (response: ApiResponse<ActionResponse>, fallbackError: string) => {
  if ((response.Code ?? response.code) === 1) {
    const data = response.data as ActionResponse | string | undefined;
    if (typeof data === 'string') {
      return data;
    }
    return data?.message;
  }

  throw new Error(extractErrorMessage(response, fallbackError));
};

const invalidateGridsAfterSuccess = async (
  queryFulfilled: Promise<unknown>,
  dispatch: Parameters<typeof invalidateGridDataCache>[0]
) => {
  try {
    await queryFulfilled;
    invalidateGridDataCache(dispatch);
  } catch {
    // Keep existing error handling on the caller; do not invalidate on failure.
  }
};

export const financeEmailHistoryActionApi = createApi({
  reducerPath: 'financeEmailHistoryActionApi',
  baseQuery,
  endpoints: (builder) => ({
    softDeleteFinanceEmails: builder.mutation<string | undefined, string[]>({
      query: (historyIds) => ({
        url: `${FINANCE_EMAIL_HISTORY_ACTION_API}/SoftDelete`,
        method: 'POST',
        body: { historyIds },
      }),
      transformResponse: (response: ApiResponse<ActionResponse>) =>
        parseActionResponse(response, 'Failed to delete finance emails.'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateGridsAfterSuccess(queryFulfilled, dispatch);
      },
    }),
    restoreFinanceEmails: builder.mutation<string | undefined, string[]>({
      query: (historyIds) => ({
        url: `${FINANCE_EMAIL_HISTORY_ACTION_API}/Restore`,
        method: 'POST',
        body: { historyIds },
      }),
      transformResponse: (response: ApiResponse<ActionResponse>) =>
        parseActionResponse(response, 'Failed to restore finance emails.'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateGridsAfterSuccess(queryFulfilled, dispatch);
      },
    }),
    markFinanceEmailsSpam: builder.mutation<string | undefined, string[]>({
      query: (historyIds) => ({
        url: `${FINANCE_EMAIL_HISTORY_ACTION_API}/MarkSpam`,
        method: 'POST',
        body: { historyIds },
      }),
      transformResponse: (response: ApiResponse<ActionResponse>) =>
        parseActionResponse(response, 'Failed to mark finance emails as spam.'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateGridsAfterSuccess(queryFulfilled, dispatch);
      },
    }),
    unmarkFinanceEmailsSpam: builder.mutation<string | undefined, string[]>({
      query: (historyIds) => ({
        url: `${FINANCE_EMAIL_HISTORY_ACTION_API}/UnmarkSpam`,
        method: 'POST',
        body: { historyIds },
      }),
      transformResponse: (response: ApiResponse<ActionResponse>) =>
        parseActionResponse(response, 'Failed to unmark finance emails as spam.'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateGridsAfterSuccess(queryFulfilled, dispatch);
      },
    }),
    addFinanceEmailSenderToSpamList: builder.mutation<string | undefined, string>({
      query: (historyId) => ({
        url: `${FINANCE_EMAIL_HISTORY_ACTION_API}/AddSenderToSpamList`,
        method: 'POST',
        body: { historyIds: [historyId] },
      }),
      transformResponse: (response: ApiResponse<ActionResponse>) =>
        parseActionResponse(response, 'Failed to add sender to spam list.'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await invalidateGridsAfterSuccess(queryFulfilled, dispatch);
      },
    }),
  }),
});
